import styled, { keyframes } from 'styled-components';

import {
  AFFILIATE_FORM,
  AFFILIATE_FORM_ANIM,
  CASINO_TECH_STEP,
  COLORS,
  GRADIENTS,
  MOBILE,
  REDUCED_MOTION,
  type DeploymentStepState,
} from '../../../../../theme/tokens';
import { Text } from '../../../../../components';

// A thin LOCAL mirror of the casino-settings Overlay shell (centred modal on
// desktop, bottom sheet on mobile), against the SAME affiliate-form tokens — kept
// local so the home section stays decoupled. Logic is byte-faithful to the source.
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;
const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;
const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.96); }
  to { opacity: 1; transform: scale(1); }
`;
const scaleOut = keyframes`
  from { opacity: 1; transform: scale(1); }
  to { opacity: 0; transform: scale(0.96); }
`;
const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;
const slideDown = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(100%); }
`;

export const Backdrop = styled.div<{ $closing?: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: ${AFFILIATE_FORM.backdrop};
  animation: ${(p) => (p.$closing ? fadeOut : fadeIn)} ${AFFILIATE_FORM_ANIM.BACKDROP_MS}ms ${AFFILIATE_FORM_ANIM.EASE}
    both;

  &,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  ${MOBILE} {
    padding: 0;
    align-items: stretch;
    justify-content: stretch;
  }

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

export const Sheet = styled.div<{ $closing?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: min(520px, 100%);
  max-height: min(680px, 100%);
  overflow: hidden;
  border-radius: 16px;
  background: ${AFFILIATE_FORM.surface};
  box-shadow: ${AFFILIATE_FORM.modalShadow};
  animation: ${(p) => (p.$closing ? scaleOut : scaleIn)} ${AFFILIATE_FORM_ANIM.SHEET_MS}ms ${AFFILIATE_FORM_ANIM.EASE}
    both;

  ${MOBILE} {
    width: 100%;
    max-height: calc(100% - 56px);
    margin-top: auto;
    border-radius: 24px 24px 0 0;
    box-shadow: ${AFFILIATE_FORM.modalShadowUp};
    animation-name: ${(p) => (p.$closing ? slideDown : slideUp)};
  }

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

export const Header = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 22px 22px 16px;
  border-bottom: 1px solid ${AFFILIATE_FORM.hairline};

  ${MOBILE} {
    padding: 20px 18px 14px;
  }
`;

export const Title = styled(Text)`
  font-size: 17px;
  font-weight: 900;
  line-height: 1.15;
  background: ${GRADIENTS.backHeaderTitle};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
`;

export const CloseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  border: none;
  border-radius: 99px;
  background: ${AFFILIATE_FORM.closeCircle};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 2px;
  }
`;

export const ScrollArea = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
`;

export const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 8px 22px 18px;
  display: flex;
  flex-direction: column;

  ${MOBILE} {
    padding: 6px 18px 16px;
  }
`;

export const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 0;
  border-bottom: 1px solid ${AFFILIATE_FORM.hairline};

  &:last-child {
    border-bottom: none;
  }
`;

export const Dot = styled.span<{ $state: DeploymentStepState }>`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => CASINO_TECH_STEP[p.$state].dot};
`;

export const RowLabel = styled(Text)`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 14px;
  font-weight: 500;
  color: ${COLORS.text};
`;

export const RowAt = styled(Text)`
  flex-shrink: 0;
  font-size: 12px;
  color: ${COLORS.textFaint};
`;

export const RowPill = styled.span<{ $state: DeploymentStepState }>`
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 500;
  color: ${(p) => CASINO_TECH_STEP[p.$state].dot};
`;

export const Footer = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 22px;
  border-top: 1px solid ${AFFILIATE_FORM.hairline};
  background: ${AFFILIATE_FORM.surface};

  ${MOBILE} {
    padding: 14px 18px calc(14px + env(safe-area-inset-bottom));
  }
`;
